import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { userService } from '../../services';
import PageHeader from '../../components/common/PageHeader';

const Profile = () => {
  const { t } = useTranslation();
  const { user: authUser, updateUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await userService.getProfile();
      const userData = response?.data || response;
      setProfile(userData);
      updateUser(userData);
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile. Using cached data.');
      setProfile(authUser);
    } finally {
      setLoading(false);
    }
  };

  const getKycStatusColor = (status) => {
    switch (status) {
      case 'VERIFIED': return 'text-green-600 bg-green-100 border-green-200';
      case 'PENDING': return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'UNDER_REVIEW': return 'text-blue-600 bg-blue-100 border-blue-200';
      case 'REJECTED': return 'text-red-600 bg-red-100 border-red-200';
      default: return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary-50">
        <div className="relative w-20 h-20">
          <div className="absolute top-0 left-0 w-full h-full border-4 border-blue-200 rounded-full"></div>
          <div className="absolute top-0 left-0 w-full h-full border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50 pb-12">
      <PageHeader
        title={profile?.fullName || 'User Profile'}
        subtitle={profile?.email}
        icon={
          <div className="w-12 h-12 rounded-full bg-primary-600 flex items-center justify-center text-white font-bold text-xl">
            {getInitials(profile?.fullName)}
          </div>
        }
        extra={
          <div className="flex gap-3">
            <Link to="/my-donations" className="bg-white hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg font-medium border border-gray-200 shadow-sm transition-all flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="9" strokeWidth="2" />
                <text x="12" y="15.5" textAnchor="middle" fontSize="10" fontWeight="bold" fill="currentColor" stroke="none">रु</text>
              </svg>
              {t('profile.myDonations')}
            </Link>
          </div>
        }
      />

      <div className="max-w-6xl mx-auto px-4 mt-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Info Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Account Overview Card */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden transform transition-all hover:shadow-md">
              <div className="p-6 md:p-8">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <span className="w-1.5 h-6 bg-blue-600 rounded-full"></span>
                    {t('profile.personalDetails')}
                  </h3>
                  {/* <button className="text-blue-600 hover:text-blue-700 text-sm font-semibold flex items-center gap-1 group transition-all">
                    <svg className="w-4 h-4 transition-transform group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    Edit Profile
                  </button> */}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-1 group">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block transform transition-all group-hover:text-blue-500">{t('form.fullName')}</label>
                    <p className="text-gray-900 font-semibold text-lg">{profile?.fullName}</p>
                  </div>
                  <div className="space-y-1 group">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block transform transition-all group-hover:text-blue-500">{t('form.email')}</label>
                    <p className="text-gray-900 font-semibold">{profile?.email}</p>
                  </div>
                  <div className="space-y-1 group">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block transform transition-all group-hover:text-blue-500">{t('form.phoneNumber')}</label>
                    <p className="text-gray-900 font-semibold">{profile?.phoneNumber || t('common.notProvided')}</p>
                  </div>
                  <div className="space-y-1 group">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block transform transition-all group-hover:text-blue-500">{t('form.country')}</label>
                    <p className="text-gray-900 font-semibold">{profile?.country || 'Nepal'}</p>
                  </div>
                  <div className="space-y-1 group">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest block transform transition-all group-hover:text-blue-500">{t('profile.registeredDate')}</label>
                    <p className="text-gray-900 font-semibold">{new Date(profile?.createdAt || Date.now()).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                </div>
              </div>
            </div>


            {/* Quick Links / Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Link to="/my-donations" className="group bg-white p-8 rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-blue-200 transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-all duration-300">
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                    </svg>
                  </div>
                  <div className="px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity">{t('actions.viewAll')}</div>
                </div>
                <h4 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{t('profile.myDonations')}</h4>
                <p className="text-sm text-gray-500 mt-2 leading-relaxed">{t('profile.myDonationsDescription')}</p>
              </Link>

              <Link to="/reports" className="group bg-white p-8 rounded-3xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-indigo-200 transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-all duration-300">
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <div className="px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity">{t('actions.view')}</div>
                </div>
                <h4 className="text-xl font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">{t('reports.title')}</h4>
                <p className="text-sm text-gray-500 mt-2 leading-relaxed">{t('profile.reportsDescription')}</p>
              </Link>
            </div>
          </div>

          {/* Sidebar Column */}
          <div className="space-y-8">
            {/* KYC Status Card */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <span className="w-1.5 h-6 bg-blue-600 rounded-full"></span>
                {t('profile.verificationStatus')}
              </h3>

              <div className={`p-6 rounded-2xl border-2 flex flex-col items-center text-center ${getKycStatusColor(profile?.kycStatus)}`}>
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${profile?.kycStatus === 'VERIFIED' ? 'bg-green-200' :
                  profile?.kycStatus === 'REJECTED' ? 'bg-red-200' : 'bg-blue-200'
                  }`}>
                  {profile?.kycStatus === 'VERIFIED' ? (
                    <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  )}
                </div>
                <h4 className="font-bold text-lg uppercase tracking-tight">{profile?.kycStatus || 'PENDING'}</h4>
                <p className="text-sm mt-2 font-medium opacity-80">
                  {profile?.kycStatus === 'VERIFIED' ? t('profile.accountVerified') : t('profile.verificationRequired')}
                </p>
                {profile?.kycStatus !== 'VERIFIED' && (
                  <button
                    onClick={() => navigate('/check-kyc-status')}
                    className="mt-6 w-full py-3 bg-white text-blue-600 rounded-xl font-bold shadow-sm hover:shadow-md transition-all border border-blue-100"
                  >
                    {t('profile.manageKyc')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;

